import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { CreditCard } from 'lucide-react'
import { Roles } from '../auth/rbac'
import {
  ENABLE_USER_BILLING,
} from '../config/featureFlags'
import { useAuth } from '../auth/AuthContext'
import { api, getApiErrorMessage } from '../lib/api'
import {
  billingStatusToneClass,
  formatBillingDocumentTypeLabel,
  formatBillingModeLabel,
  formatBillingProviderLabel,
  formatBillingStatusLabel,
} from '../lib/billingLabels'
import { formatDate, formatDateTimeMinute } from '../lib/date'

const TABS = [
  { id: 'summary', label: '내 구독', path: '/billing' },
  { id: 'orders', label: '결제내역', path: '/billing/orders' },
  { id: 'refunds', label: '환불내역', path: '/billing/refunds' },
  { id: 'documents', label: '청구/증빙', path: '/billing/documents', orgOnly: true },
  { id: 'org', label: '조직 구독', path: '/billing/org', orgOnly: true },
]

const ORG_BILLING_ROLES = new Set([
  Roles.OPERATOR,
  Roles.GA_MASTER_ADMIN,
  Roles.GA_MASTER_DEPUTY_ADMIN,
  Roles.GA_LEADER,
  Roles.GA_DEPUTY_LEADER,
  Roles.GA_MANAGER,
  Roles.GA_BRANCH_MANAGER,
  Roles.GA_DEPUTY_BRANCH_MANAGER,
  Roles.GA_OTHER_ADMIN,
  Roles.INSURER_MASTER_ADMIN,
  Roles.INSURER_MASTER_DEPUTY_ADMIN,
  Roles.INSURER_LEADER,
  Roles.INSURER_DEPUTY_LEADER,
  Roles.INSURER_MANAGER,
  Roles.INSURER_BRANCH_MANAGER,
  Roles.INSURER_DEPUTY_BRANCH_MANAGER,
  Roles.INSURER_OTHER_ADMIN,
])

export default function UserBillingPage() {
  const { user } = useAuth()
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [summary, setSummary] = useState(null)
  const [orders, setOrders] = useState([])
  const [refunds, setRefunds] = useState([])
  const [documents, setDocuments] = useState([])
  const [orgBilling, setOrgBilling] = useState(null)
  const [orgSeats, setOrgSeats] = useState(null)
  const [selectedOrgScopeId, setSelectedOrgScopeId] = useState('ALL')
  const [error, setError] = useState('')

  const isOrgAdmin = useMemo(() => {
    const roles = user?.roles || []
    return roles.some((role) => ORG_BILLING_ROLES.has(role))
  }, [user])

  const activeTab = useMemo(() => {
    const found = TABS.find((tab) => tab.path === location.pathname)
    return found?.id || 'summary'
  }, [location.pathname])

  const canViewOrgBilling = useMemo(() => (
    isOrgAdmin && (
      summary?.orgContractPrimary === true
      || Boolean(orgBilling?.contract)
      || Boolean(orgBilling?.orgName || orgBilling?.orgId || orgBilling?.id)
      || Boolean(orgSeats?.contract)
    )
  ), [isOrgAdmin, orgBilling, orgSeats, summary])

  const visibleTabs = useMemo(() => TABS.filter((tab) => !tab.orgOnly || canViewOrgBilling), [canViewOrgBilling])
  const activeTabMeta = useMemo(() => TABS.find((tab) => tab.id === activeTab), [activeTab])

  const load = useCallback(async (cancelled = false) => {
    setLoading(true)
    setError('')
    try {
      const billingRequestConfig = () => ({ __silent: true, __suppressErrorPageRedirect: true })
      const requests = [
        api.get('/billing/me', billingRequestConfig()),
        api.get('/billing/me/orders', billingRequestConfig()),
        api.get('/billing/me/refunds', billingRequestConfig()),
        api.get('/billing/me/documents', billingRequestConfig()).catch(() => ({ data: { data: [] } })),
        isOrgAdmin ? api.get('/billing/org/me', billingRequestConfig()).catch(() => ({ data: { data: null } })) : Promise.resolve({ data: { data: null } }),
        isOrgAdmin ? api.get('/billing/org/me/seats', billingRequestConfig()).catch(() => ({ data: { data: null } })) : Promise.resolve({ data: { data: null } }),
      ]
      const [summaryRes, ordersRes, refundsRes, docsRes, orgRes, seatsRes] = await Promise.all(requests)
      if (cancelled) return
      setSummary(summaryRes?.data?.data ?? null)
      setOrders(Array.isArray(ordersRes?.data?.data) ? ordersRes.data.data : [])
      setRefunds(Array.isArray(refundsRes?.data?.data) ? refundsRes.data.data : [])
      setDocuments(Array.isArray(docsRes?.data?.data) ? docsRes.data.data : [])
      setOrgBilling(orgRes?.data?.data ?? null)
      setOrgSeats(seatsRes?.data?.data ?? null)
    } catch (e) {
      if (!cancelled) setError(getApiErrorMessage(e, '구독/결제 정보를 불러오지 못했습니다.'))
    } finally {
      if (!cancelled) setLoading(false)
    }
  }, [isOrgAdmin])

  useEffect(() => {
    let cancelled = false
    async function run() {
      await load(cancelled)
    }
    run()
    return () => { cancelled = true }
  }, [load])

  useEffect(() => {
    if (selectedOrgScopeId === 'ALL') return
    const options = Array.isArray(orgSeats?.orgOptions) ? orgSeats.orgOptions : []
    const selectedExists = options.some((option) => String(option.orgId) === String(selectedOrgScopeId))
    if (!selectedExists) {
      setSelectedOrgScopeId('ALL')
    }
  }, [orgSeats, selectedOrgScopeId])

  if (!ENABLE_USER_BILLING) {
    return <Navigate to="/profile" replace state={{ billingHidden: true }} />
  }

  const subscription = summary?.subscription || {}
  const features = Array.isArray(summary?.features) ? summary.features : []
  const currentQuotaPeriod = subscription.currentQuotaPeriod || null
  const quotaCounterMap = new Map(
    Array.isArray(currentQuotaPeriod?.counters)
      ? currentQuotaPeriod.counters.map((item) => [normalizeBillingFeatureCode(item.featureCode), item])
      : [],
  )
  const billingFeatureRows = orderedUserBillingFeatures(features)
  const orgContract = summary?.orgContract || null
  const orgBillingContract = orgBilling?.contract || orgBilling || orgSeats?.contract || null
  const orgScopeOptions = Array.isArray(orgSeats?.orgOptions) ? orgSeats.orgOptions : []
  const orgMemberRows = Array.isArray(orgSeats?.members) ? orgSeats.members : []
  const visibleOrgMembers = selectedOrgScopeId === 'ALL'
    ? orgMemberRows
    : orgMemberRows.filter((member) => String(member.orgId) === String(selectedOrgScopeId))
  const selectedOrgOption = selectedOrgScopeId === 'ALL'
    ? null
    : orgScopeOptions.find((option) => String(option.orgId) === String(selectedOrgScopeId))
  const selectedApprovedMemberCount = selectedOrgScopeId === 'ALL'
    ? (orgSeats?.scopeApprovedMemberCount ?? orgSeats?.approvedMemberCount ?? orgMemberRows.length)
    : (selectedOrgOption?.approvedMemberCount ?? visibleOrgMembers.length)
  const orgContractPrimary = summary?.orgContractPrimary === true
  const transitionNotice = summary?.transitionNotice || null

  if (!loading && !error && activeTabMeta?.orgOnly && !canViewOrgBilling) {
    return <Navigate to="/billing" replace />
  }

  const summaryPlanLabel = orgContractPrimary
    ? (orgContract?.appliedPlanCode ? formatPlanCodeLabel(orgContract.appliedPlanCode) : '조직 계약')
    : (subscription.planName || formatPlanCodeLabel(subscription.planCode) || '-')

  const summaryStatusNode = orgContractPrimary
    ? (
      <span className="inline-flex rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-medium text-sky-800 ring-1 ring-sky-200">
        조직 결제 적용
      </span>
      )
    : <StatusBadge value={subscription.status} type="subscription" />

  const summaryEndsAt = orgContractPrimary ? orgContract?.endsAt : subscription.endsAt

  return (
    <section>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">구독/결제</h1>
        </div>
        <Link
          to="/billing/subscribe"
          className="inline-flex min-h-[2.5rem] items-center justify-center gap-2 rounded bg-brand-navy px-3 text-sm font-semibold text-white hover:opacity-90"
        >
          <CreditCard className="h-4 w-4" aria-hidden="true" />
          구독하기
        </Link>
      </div>

      <div className="mt-6 border-b">
        <nav className="flex flex-wrap gap-x-6">
          {visibleTabs.map((tab) => (
            <Link
              key={tab.id}
              to={tab.path}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
              {tab.label}
            </Link>
          ))}
        </nav>
      </div>

      {loading && <div className="mt-8 text-slate-500">로딩 중...</div>}
      {!loading && error && <div className="mt-8 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {!loading && !error && activeTab === 'summary' && (
        <div className="mt-6 space-y-6">
          {orgContractPrimary && (
            <div className="rounded border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
              <div className="font-semibold">조직 단위 이용 중</div>
              <div className="mt-1 text-sky-800">
                소속 조직의 조직 결제 계약이 적용 중입니다. 아래 요약은 <strong>조직 계약</strong>을 우선으로 표시합니다.
                {subscription?.status === 'EXPIRED' && (
                  <span className="mt-2 block text-xs text-slate-600">
                    개인 구독은 만료 상태로 남아 있을 수 있습니다. 상단 요약은 조직 계약 기준입니다.
                  </span>
                )}
              </div>
            </div>
          )}
          {orgContract?.status === 'TERMINATING' && (
            <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <div className="font-semibold">조직 계약 종료 유예가 진행 중입니다.</div>
              <div className="mt-1">
                현재 조직 계약은 {formatDateTimeMinute(orgContract.graceUntil)} 까지 한시 적용됩니다. 이후에는 개인 결제 기준으로 전환될 수 있으니 결제수단과 구독 상태를 확인해 주세요.
              </div>
            </div>
          )}
          {transitionNotice?.message && (
            <div className="rounded border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
              <div className="font-semibold">개인 결제 전환 안내</div>
              <div className="mt-1">{transitionNotice.message}</div>
              {transitionNotice.promotionCode && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="rounded bg-white px-2 py-1 font-mono text-xs text-sky-800 ring-1 ring-sky-200">
                    {transitionNotice.promotionCode}
                  </span>
                  {transitionNotice.promotionValidTo && (
                    <span className="text-xs text-sky-700">
                      사용 기한: {formatDateTimeMinute(transitionNotice.promotionValidTo)}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <SummaryCard label={orgContractPrimary ? '적용 플랜 (조직)' : '현재 플랜'} value={summaryPlanLabel} />
            <SummaryCard label={orgContractPrimary ? '이용 상태' : '상태'} valueNode={summaryStatusNode} />
            <SummaryCard label={orgContractPrimary ? '계약 종료일' : '만료일'} value={formatDate(summaryEndsAt)} />
            <SummaryCard label="과금 방식" value={formatBillingModeLabel(summary?.billingMode)} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SummaryCard label="현재 기간 시작" value={formatDate(currentQuotaPeriod?.periodStartAt)} />
            <SummaryCard label="현재 기간 종료" value={formatDate(currentQuotaPeriod?.periodEndAt)} />
            <SummaryCard label="다음 리셋" value={formatDate(subscription.nextResetAt)} />
          </div>

          <div className="rounded border bg-white p-5">
            <h2 className="text-lg font-semibold">기능별 잔여 횟수</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-4 py-3 text-left">기능</th>
                    <th className="px-4 py-3 text-left">잔여/전체</th>
                  </tr>
                </thead>
                <tbody>
                  {billingFeatureRows.map((feature) => {
                    const counter = quotaCounterMap.get(normalizeBillingFeatureCode(feature.featureCode))
                    return (
                    <tr key={feature.featureCode} className="border-t">
                      <td className="px-4 py-3 font-medium">{formatUserBillingFeatureName(feature)}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900">{formatUserBillingFeatureRemaining(feature, counter)}</td>
                    </tr>
                    )
                  })}
                  {billingFeatureRows.length === 0 && (
                    <tr>
                      <td className="px-4 py-8 text-center text-slate-500" colSpan={2}>표시할 기능별 잔여 횟수가 없습니다.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {!loading && !error && activeTab === 'orders' && (
        <SimpleTable
          title="결제내역"
          columns={['주문번호', '플랜', '금액', '결제일시', '영수증']}
          rows={orders.map((order) => [
            order.orderNo || '-',
            formatUserBillingPlanName(order),
            currency(order.amount),
            formatDateTimeMinute(order.paidAt || order.approvedAt || order.paymentApprovedAt),
            renderExternalLink(order.receiptUrl, '보기'),
          ])}
          emptyText="결제내역이 없습니다."
        />
      )}

      {!loading && !error && activeTab === 'refunds' && (
        <SimpleTable
          title="환불내역"
          columns={['환불번호', '주문번호', '결제ID', '금액', '상태', '사유', '요청일시']}
          rows={refunds.map((refund) => [
            refund.refundNo || refund.id,
            refund.orderNo || refund.orderId || '-',
            refund.paymentId || '-',
            currency(refund.amount),
            <StatusBadge value={refund.status} type="refund" />,
            refund.reason || '-',
            formatDateTimeMinute(refund.requestedAt),
          ])}
          emptyText="환불내역이 없습니다."
        />
      )}

      {!loading && !error && activeTab === 'documents' && (
        <SimpleTable
          title="청구/증빙"
          columns={['문서번호', '문서종류', '상태', '제공처', '주문번호', '링크', '발행일시']}
          rows={documents.map((doc) => [
            doc.id || doc.documentId || '-',
            formatBillingDocumentTypeLabel(doc.documentType),
            <StatusBadge value={doc.status} type="document" />,
            formatBillingProviderLabel(doc.providerType),
            doc.orderNo || doc.orderId || '-',
            renderExternalLink(doc.metadata?.receiptUrl, '보기'),
            formatDateTimeMinute(doc.issuedAt),
          ])}
          emptyText="발행된 증빙이 없습니다."
        />
      )}

      {!loading && !error && activeTab === 'org' && (
        <div className="mt-6 rounded border bg-white p-5">
          <h2 className="text-lg font-semibold">조직 구독</h2>
          {orgBillingContract ? (
            <div className="space-y-6">
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <InfoRow label="조직명" value={orgBillingContract.orgName || '-'} />
                <InfoRow label="과금 모델" value={formatBillingModeLabel(orgBillingContract.billingMode)} />
                <InfoRow
                  label="좌석 정책"
                  value={orgBillingContract.seatCountPolicyLabel || formatSeatCountPolicyLabel(orgBillingContract.seatCountPolicy)}
                />
                <InfoRow
                  label="과금 대상 인원"
                  value={String(orgSeats?.billableSeatCount ?? orgBillingContract.contractedSeats ?? orgBillingContract.currentApprovedMembers ?? '-')}
                />
                <InfoRow label="계약 기간" value={`${formatDate(orgBillingContract.startedAt)} ~ ${formatDate(orgBillingContract.endsAt)}`} />
                <InfoRow label="상태" value={<StatusBadge value={orgBillingContract.status} type="subscription" />} />
              </div>
              <div>
                <h3 className="text-base font-semibold">좌석/구성원</h3>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <InfoRow label="선택 범위 승인 조직원 수" value={String(selectedApprovedMemberCount ?? '-')} />
                  <InfoRow label="전체 과금 기준" value={String(orgSeats?.billableSeatCount ?? '-')} />
                  <InfoRow
                    label="좌석 정책"
                    value={
                      orgSeats?.seatCountPolicyLabel
                      || orgBillingContract.seatCountPolicyLabel
                      || formatSeatCountPolicyLabel(orgSeats?.seatCountPolicy || orgBillingContract.seatCountPolicy)
                    }
                  />
                </div>
                {Array.isArray(orgSeats?.seatHistory) && orgSeats.seatHistory.length > 0 && (
                  <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="px-4 py-3 text-left">변동일시</th>
                          <th className="px-4 py-3 text-left">좌석 수</th>
                          <th className="px-4 py-3 text-left">사유</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orgSeats.seatHistory.map((row, index) => (
                          <tr key={`${row.changedAt || 'seat'}-${index}`} className="border-t">
                            <td className="px-4 py-3">{formatDateTimeMinute(row.changedAt)}</td>
                            <td className="px-4 py-3">{String(row.seatCount ?? '-')}</td>
                            <td className="px-4 py-3">{row.reason || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {Array.isArray(orgSeats?.members) && (
                  <div className="mt-6 overflow-x-auto">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h3 className="text-base font-semibold">조직원 이용 현황</h3>
                      {orgScopeOptions.length > 1 && (
                        <div className="flex items-center gap-2">
                          <label className="text-sm font-medium text-slate-600" htmlFor="org-billing-scope">
                            조회 조직
                          </label>
                          <select
                            id="org-billing-scope"
                            className="min-h-[2.25rem] rounded border border-slate-300 bg-white px-3 text-sm"
                            value={selectedOrgScopeId}
                            onChange={(e) => setSelectedOrgScopeId(e.target.value)}
                          >
                            <option value="ALL">
                              전체 조직 ({orgSeats?.scopeApprovedMemberCount ?? orgSeats?.approvedMemberCount ?? orgMemberRows.length}명)
                            </option>
                            {orgScopeOptions.map((option) => (
                              <option key={String(option.orgId)} value={String(option.orgId)}>
                                {option.orgName || '조직'} ({option.approvedMemberCount ?? 0}명)
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                    <table className="mt-3 min-w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="px-4 py-3 text-left">조직</th>
                          <th className="px-4 py-3 text-left">조직원</th>
                          <th className="px-4 py-3 text-left">플랜</th>
                          <th className="px-4 py-3 text-left">구독 상태</th>
                          <th className="px-4 py-3 text-left">이용 기간</th>
                          <th className="px-4 py-3 text-left">콘텐츠 사용/잔여</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleOrgMembers.map((member, index) => (
                          <tr key={`${member.orgName || 'org'}-${member.email || member.name || 'member'}-${index}`} className="border-t align-top">
                            <td className="px-4 py-3 font-medium">{member.orgName || '-'}</td>
                            <td className="px-4 py-3">
                              <div className="font-medium">{member.name || '-'}</div>
                              <div className="mt-1 break-all text-xs text-slate-500">{member.email || '-'}</div>
                            </td>
                            <td className="px-4 py-3">{member.planName || '-'}</td>
                            <td className="px-4 py-3"><StatusBadge value={member.subscriptionStatus} type="subscription" /></td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {formatDate(member.currentPeriodStartAt)} ~ {formatDate(member.currentPeriodEndAt)}
                            </td>
                            <td className="px-4 py-3">{renderMemberUsage(member.usageSummary)}</td>
                          </tr>
                        ))}
                        {visibleOrgMembers.length === 0 && (
                          <tr>
                            <td className="px-4 py-8 text-center text-slate-500" colSpan={6}>표시할 조직원 이용 현황이 없습니다.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-4 rounded border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              현재 확인 가능한 조직 구독 계약 정보가 없습니다.
            </div>
          )}
        </div>
      )}

    </section>
  )
}

function SummaryCard({ label, value, valueNode = null }) {
  return (
    <div className="rounded border bg-white p-4">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="mt-2 text-xl font-semibold">{valueNode || value}</div>
    </div>
  )
}

function InfoRow({ label, value }) {
  return (
    <div className="rounded border p-4">
      <div className="text-slate-500">{label}</div>
      <div className="mt-2 font-medium">{value}</div>
    </div>
  )
}

function SimpleTable({ title, columns, rows, emptyText, summary = null }) {
  return (
    <div className="mt-6 rounded border bg-white p-5">
      <h2 className="text-lg font-semibold">{title}</h2>
      {summary}
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              {columns.map((column) => (
                <th key={column} className="px-4 py-3 text-left">{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={`${title}-${rowIndex}`} className="border-t">
                {row.map((cell, cellIndex) => (
                  <td key={`${title}-${rowIndex}-${cellIndex}`} className="px-4 py-3">{cell}</td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td className="px-4 py-8 text-center text-slate-500" colSpan={columns.length}>{emptyText}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function normalizeBillingFeatureCode(value) {
  return String(value || '').toUpperCase()
}

function formatUserBillingFeatureName(feature) {
  const code = normalizeBillingFeatureCode(feature?.featureCode)
  switch (code) {
    case 'CLAIM':
    case 'CLAIM_FAX':
    case 'CLAIM_INTAKE':
      return '청구'
    case 'HIDDEN_INSURANCE':
    case 'HIDDEN_INSURANCE_MONEY':
      return '숨은보험금'
    case 'HEALTH_ANALYSIS':
      return '진료내역분석'
    default:
      return feature?.featureName || '기능'
  }
}

function orderedUserBillingFeatures(features) {
  const allowedOrder = ['CLAIM_FAX', 'CLAIM', 'CLAIM_INTAKE', 'HIDDEN_INSURANCE', 'HIDDEN_INSURANCE_MONEY', 'HEALTH_ANALYSIS']
  const orderByCode = new Map(allowedOrder.map((code, index) => [code, index]))
  return features
    .filter((feature) => orderByCode.has(normalizeBillingFeatureCode(feature?.featureCode)))
    .sort((a, b) => {
      const ai = orderByCode.get(normalizeBillingFeatureCode(a?.featureCode)) ?? 999
      const bi = orderByCode.get(normalizeBillingFeatureCode(b?.featureCode)) ?? 999
      return ai - bi
    })
}

function formatUserBillingFeatureRemaining(feature, counter) {
  const quotaMode = String(counter?.quotaMode || feature?.quotaMode || '').toUpperCase()
  if (quotaMode === 'UNLIMITED') return '무제한'
  const unit = quotaUnitLabel(feature?.quotaUnit || counter?.quotaUnit)
  const remaining = numberOrNull(counter?.remainingValue ?? counter?.remaining)
  const limit = numberOrNull(counter?.limitValue ?? counter?.monthlyLimit ?? feature?.monthlyLimit)
  const used = numberOrZero(counter?.usedValue ?? counter?.used)
  const reserved = numberOrZero(counter?.reservedValue ?? counter?.reserved)
  const resolvedRemaining = remaining ?? (limit == null ? null : Math.max(0, limit - used - reserved))
  if (resolvedRemaining == null) return '-'
  if (limit == null) return `${resolvedRemaining}${unit}`
  return `${resolvedRemaining}/${limit}${unit}`
}

function quotaUnitLabel(quotaUnit) {
  switch (quotaUnit) {
    case 'CASE':
    case 'COUNT':
      return '건'
    case 'RECIPIENT':
      return '명'
    case 'RUN':
      return '회'
    default:
      return ''
  }
}

function renderMemberUsage(items) {
  const rows = Array.isArray(items) ? items : []
  if (rows.length === 0) return '-'
  return (
    <div className="space-y-1">
      {rows.map((item, index) => (
        <div key={`${item.featureName || 'feature'}-${index}`} className="flex flex-wrap gap-x-2 gap-y-1">
          <span className="font-medium">{item.featureName || '콘텐츠'}</span>
          <span className="text-slate-600">{formatMemberUsageCounter(item)}</span>
        </div>
      ))}
    </div>
  )
}

function formatMemberUsageCounter(item) {
  const unit = quotaUnitLabel(item?.quotaUnit)
  const used = numberOrZero(item?.usedValue ?? item?.used)
  const reserved = numberOrZero(item?.reservedValue ?? item?.reserved)
  const quotaMode = String(item?.quotaMode || '').toUpperCase()
  if (quotaMode === 'UNLIMITED') {
    return `${used}${unit} 사용 / 무제한`
  }
  const limit = numberOrNull(item?.limitValue ?? item?.monthlyLimit)
  const remaining = numberOrNull(item?.remainingValue ?? item?.remaining)
  const resolvedRemaining = remaining ?? (limit == null ? null : Math.max(0, limit - used - reserved))
  const reservedText = reserved > 0 ? ` / ${reserved}${unit} 예약` : ''
  return `${used}${unit} 사용 / ${resolvedRemaining == null ? '-' : `${resolvedRemaining}${unit}`} 잔여${reservedText}`
}

function numberOrZero(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function numberOrNull(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function formatSeatCountPolicyLabel(value) {
  if (!value) return '-'
  const normalized = String(value).toUpperCase()
  if (normalized === 'APPROVED_MEMBER_COUNT') return '승인된 조직원 수'
  if (normalized === 'CONTRACTED_SEAT_COUNT') return '계약 좌석 수'
  return normalized.includes('_') ? '미분류' : String(value)
}

const PLAN_CODE_LABELS = {
  BASIC: '베이직',
  STANDARD: '스탠다드',
  PREMIUM: '프리미엄',
  ENTERPRISE: '엔터프라이즈',
}

function formatPlanCodeLabel(value) {
  if (!value) return '-'
  const normalized = String(value).toUpperCase()
  return PLAN_CODE_LABELS[normalized] || '구독 플랜'
}

function formatUserBillingPlanName(order) {
  return order?.planName || order?.plan?.name || formatPlanCodeLabel(order?.planCode)
}

function currency(value) {
  if (typeof value !== 'number') return '-'
  return `₩${value.toLocaleString()}`
}

function StatusBadge({ value, type = 'default' }) {
  const text = formatBillingStatusLabel(value, type)
  const tone = billingStatusToneClass(value, type)
  return <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${tone}`}>{text}</span>
}

function renderExternalLink(url, label) {
  if (!url) return '-'
  return (
    <a className="text-xs text-brand-navy underline" href={url} target="_blank" rel="noreferrer">
      {label}
    </a>
  )
}
